import { assertIsDefined } from '@pflegia/util/functions/type-guards';

import { BaseFunnelAnswers, BaseFunnelQuestion, BaseFunnelJobTitle, FunnelJobTitleAnswer } from '../types';

/**
 * Returns selected job title and step it is from from answers.
 *
 * If question key is given, returns the answer given before this found question.
 * This is needed when going back in steps.
 */
export const getSelectedJobTitleAndStep = <
  J extends BaseFunnelJobTitle = BaseFunnelJobTitle,
  A extends BaseFunnelAnswers<J> = BaseFunnelAnswers<J>,
  Q extends string = string,
>(
  answers: A,
  question?: Q,
): FunnelJobTitleAnswer<J> => {
  const jobTitleAnswers = answers[BaseFunnelQuestion.PositionDesired];
  assertIsDefined(jobTitleAnswers, 'Job title answers');
  return getSelectedJobTitleAndStepFromAnswers(jobTitleAnswers, question);
};

/**
 * Returns selected job title from answers.
 *
 * If question key is given, returns the answer given before this found question.
 * This is needed when going back in steps.
 */
export const getSelectedJobTitle = <
  J extends BaseFunnelJobTitle = BaseFunnelJobTitle,
  A extends BaseFunnelAnswers<J> = BaseFunnelAnswers<J>,
  Q extends string = string,
>(
  answers: A,
  question?: Q,
): J => getSelectedJobTitleAndStep<J, A, Q>(answers, question).jobTitle;

/**
 * Returns selected job title and step it is from from job title answers.
 *
 * If question key is given, returns the answer given before this found question.
 * This is needed when going back in steps.
 */
export const getSelectedJobTitleAndStepFromAnswers = <J extends BaseFunnelJobTitle = BaseFunnelJobTitle, Q extends string = string>(
  jobTitleAnswers: FunnelJobTitleAnswer<J>[],
  question?: Q,
): FunnelJobTitleAnswer<J> => {
  const originalAnswerIndex = question ? jobTitleAnswers.findIndex(jobTitleAnswer => jobTitleAnswer.question === question) : -1;
  const index = originalAnswerIndex === -1 ? jobTitleAnswers.length - 1 : Math.max(0, originalAnswerIndex - 1);
  const selected = jobTitleAnswers[index];
  assertIsDefined(selected, 'Job title');
  return selected;
};

/**
 * Returns selected job title from job title answers.
 *
 * If question key is given, returns the answer given before this found question.
 * This is needed when going back in steps.
 */
export const getSelectedJobTitleFromAnswers = <J extends BaseFunnelJobTitle = BaseFunnelJobTitle, Q extends string = string>(
  jobTitleAnswers: FunnelJobTitleAnswer<J>[],
  question?: Q,
): J => getSelectedJobTitleAndStepFromAnswers(jobTitleAnswers, question).jobTitle;
